import i18n from '../i18n/config';
import { localizedPath } from '../i18n/path';
import type {
  Advantage,
  ApplicationProps,
  Audience,
  AudienceProps,
  Benefit,
  BlogCategory,
  Brand,
  ContactsContent,
  ContactsProps,
  FaqEntry,
  FieldType,
  FormFieldDefinition,
  HomeHero,
  HomeProps,
  Post,
  PostSummary,
  Pricing,
  Product,
  ProductFormDefinition,
  ProductHero,
  ProductSummary,
  SharedData,
  StaticPageContent,
  Step,
  Testimonial,
} from '../types';
import {
  buildContentBlocks,
  isMissing,
  isPublished,
  localeRoot,
  orderedSlugs,
  readObject,
  readingMinutes,
} from './helpers';
import { t } from '../i18n/t';

type RawBrand = {
  name?: string;
  legal_name?: string;
  tagline?: string;
  broker_label?: string;
  email?: string;
  phone?: string;
  phone_e164?: string;
  working_hours?: string;
  address?: string;
  registration_number?: string | number;
};

type RawFormField = {
  name: string;
  label: string;
  type: string;
  required?: boolean;
  options?: string[];
  placeholder?: string | number;
  hint?: string;
  unit?: string;
  show_when?: { field: string; values: string[] };
};

type RawProduct = {
  name?: string;
  short_name?: string;
  internal_name?: string;
  category?: string;
  audiences?: string[];
  icon?: string;
  tagline?: string;
  summary?: string;
  hero?: { title?: string; subtitle?: string; highlights?: string[] };
  benefits?: Benefit[];
  covered?: string[];
  not_covered?: string[];
  pricing?: {
    intro?: string;
    examples?: { label: string; price: string; note?: string }[];
    factors?: string[];
  };
  faq?: FaqEntry[];
  related?: string[];
  form_fields?: RawFormField[];
};

type RawBlog = {
  title?: string;
  category?: string;
  excerpt?: string;
  author?: string;
  published_at?: string;
  tags?: string[];
  related_product?: string;
  body?: unknown;
};

type RawPage = {
  title?: string;
  subtitle?: string;
  updated_on?: string;
  body?: unknown;
};

type RawCategory = {
  name?: string;
  description?: string;
  position?: number;
};

function productsMap(): Record<string, RawProduct> {
  const products = localeRoot().products;
  return products && typeof products === 'object' && !Array.isArray(products)
    ? (products as Record<string, RawProduct>)
    : {};
}

function blogsMap(): Record<string, RawBlog> {
  const blogs = localeRoot().blogs;
  return blogs && typeof blogs === 'object' && !Array.isArray(blogs)
    ? (blogs as Record<string, RawBlog>)
    : {};
}

function pagesMap(): Record<string, RawPage> {
  const pages = localeRoot().pages;
  return pages && typeof pages === 'object' && !Array.isArray(pages)
    ? (pages as Record<string, RawPage>)
    : {};
}

function categoriesMap(): Record<string, RawCategory> {
  const categories = localeRoot().categories;
  return categories && typeof categories === 'object' && !Array.isArray(categories)
    ? (categories as Record<string, RawCategory>)
    : {};
}

function productPath(slug: string): string {
  return localizedPath(`/apdrosinasana/${slug}`);
}

function blogPath(slug: string): string {
  return localizedPath(`/blogs/${slug}`);
}

function categoryPath(slug: string): string {
  return localizedPath(`/blogs/kategorija/${slug}`);
}

function pagePath(slug: string): string {
  return localizedPath(`/${slug}`);
}

function buildFormField(raw: RawFormField): FormFieldDefinition {
  return {
    name: raw.name,
    label: raw.label,
    type: raw.type as FieldType,
    required: Boolean(raw.required),
    ...(raw.options ? { options: raw.options } : {}),
    ...(raw.placeholder != null && raw.placeholder !== ''
      ? { placeholder: String(raw.placeholder) }
      : {}),
    ...(raw.hint ? { hint: raw.hint } : {}),
    ...(raw.unit ? { unit: raw.unit } : {}),
    ...(raw.show_when?.field && Array.isArray(raw.show_when.values)
      ? { showWhen: { field: raw.show_when.field, values: raw.show_when.values.map(String) } }
      : {}),
  };
}

function buildHero(raw: RawProduct['hero']): ProductHero {
  return {
    title: raw?.title ?? '',
    subtitle: raw?.subtitle ?? '',
    highlights: Array.isArray(raw?.highlights) ? raw.highlights.map(String) : [],
  };
}

function buildPricing(raw: RawProduct['pricing']): Pricing {
  return {
    intro: raw?.intro ?? '',
    examples: Array.isArray(raw?.examples)
      ? raw.examples.map((example) => ({
          label: example.label,
          price: example.price,
          ...(example.note ? { note: example.note } : {}),
        }))
      : [],
    factors: Array.isArray(raw?.factors) ? raw.factors.map(String) : [],
  };
}

function buildProductSummary(slug: string, raw?: RawProduct): ProductSummary | null {
  if (!raw || isMissing(raw.name)) return null;

  return {
    slug,
    internalName: raw.internal_name ?? slug,
    name: raw.name!,
    shortName: raw.short_name ?? raw.name!,
    category: raw.category ?? '',
    audiences: (raw.audiences ?? []) as Audience[],
    icon: raw.icon ?? 'shield',
    tagline: raw.tagline ?? '',
    summary: raw.summary ?? '',
    path: productPath(slug),
  };
}

function buildCategory(slug: string, raw?: RawCategory): BlogCategory | null {
  if (!raw || isMissing(raw.name)) return null;

  return {
    slug,
    name: raw.name!,
    description: raw.description ?? '',
    path: categoryPath(slug),
  };
}

function buildPostSummary(slug: string, raw?: RawBlog): PostSummary | null {
  if (!raw || isMissing(raw.title) || isMissing(raw.published_at)) return null;

  const category = raw.category ? getCategory(raw.category) : null;
  const body = buildContentBlocks(raw.body);

  return {
    slug,
    title: raw.title!,
    excerpt: raw.excerpt ?? '',
    path: blogPath(slug),
    author: raw.author ?? getBrand().name,
    publishedAt: raw.published_at!,
    readingMinutes: readingMinutes(body),
    category,
  };
}

export function getBrand(): Brand {
  const raw = readObject<RawBrand>('brand') ?? (localeRoot().brand as RawBrand | undefined) ?? {};

  return {
    name: raw.name ?? 'apdrosinasanai.kreditiem.lv',
    legalName: raw.legal_name ?? '',
    tagline: raw.tagline ?? '',
    brokerLabel: raw.broker_label ?? t('brand.broker_label'),
    email: raw.email ?? '',
    phone: raw.phone ?? '',
    phoneHref: raw.phone_e164 ? `tel:${raw.phone_e164}` : '',
    workingHours: raw.working_hours ?? '',
    address: raw.address ?? '',
    registrationNumber: raw.registration_number != null ? String(raw.registration_number) : '',
  };
}

export function listProducts(): ProductSummary[] {
  const slugs = orderedSlugs('product_order');
  const map = productsMap();
  const seen = new Set<string>();

  const ordered = slugs
    .map((slug) => {
      seen.add(slug);
      return buildProductSummary(slug, map[slug]);
    })
    .filter((product): product is ProductSummary => product != null);

  const remainder = Object.keys(map)
    .filter((slug) => !seen.has(slug))
    .sort()
    .map((slug) => buildProductSummary(slug, map[slug]))
    .filter((product): product is ProductSummary => product != null);

  return [...ordered, ...remainder];
}

export function getProduct(slug: string): Product | null {
  const summary = buildProductSummary(slug, productsMap()[slug]);
  if (!summary) return null;

  const raw = productsMap()[slug];
  const related = Array.isArray(raw?.related)
    ? raw.related
        .map((relatedSlug) => buildProductSummary(relatedSlug, productsMap()[relatedSlug]))
        .filter((product): product is ProductSummary => product != null)
    : [];

  return {
    ...summary,
    hero: buildHero(raw?.hero),
    benefits: Array.isArray(raw?.benefits) ? raw.benefits : [],
    covered: Array.isArray(raw?.covered) ? raw.covered.map(String) : [],
    notCovered: Array.isArray(raw?.not_covered) ? raw.not_covered.map(String) : [],
    pricing: buildPricing(raw?.pricing),
    faq: Array.isArray(raw?.faq) ? raw.faq : [],
    formFields: Array.isArray(raw?.form_fields) ? raw.form_fields.map(buildFormField) : [],
    related,
  };
}

export function productsFor(audience: Audience): ProductSummary[] {
  return listProducts().filter((product) => product.audiences.includes(audience));
}

export function formOptions(): ApplicationProps['options'] {
  const form = readObject<{ other_slug?: string }>('form') ?? {};
  const otherSlug = form.other_slug ?? 'other';
  const types = readObject<Record<string, string>>('form.insurance_types') ?? {};

  return [
    ...listProducts().map((product) => ({
      value: product.internalName,
      label: types[product.internalName] ?? product.shortName,
    })),
    { value: otherSlug, label: types.other ?? t('form.other_label') },
  ];
}

export function formProducts(): ProductFormDefinition[] {
  const map = productsMap();

  return listProducts().map((summary) => {
    const raw = map[summary.slug];
    return {
      slug: summary.slug,
      internalName: summary.internalName,
      name: summary.name,
      audiences: summary.audiences,
      formFields: Array.isArray(raw?.form_fields) ? raw.form_fields.map(buildFormField) : [],
    };
  });
}

export function listCategories(): BlogCategory[] {
  const slugs = orderedSlugs('category_order');
  const map = categoriesMap();
  const seen = new Set<string>();

  const ordered = slugs
    .map((slug) => {
      seen.add(slug);
      return buildCategory(slug, map[slug]);
    })
    .filter((category): category is BlogCategory => category != null)
    .sort((left, right) => {
      const leftPos = map[left.slug]?.position ?? 99;
      const rightPos = map[right.slug]?.position ?? 99;
      return leftPos - rightPos;
    });

  const remainder = Object.keys(map)
    .filter((slug) => !seen.has(slug))
    .sort()
    .map((slug) => buildCategory(slug, map[slug]))
    .filter((category): category is BlogCategory => category != null);

  return [...ordered, ...remainder];
}

export function getCategory(slug: string): BlogCategory | null {
  return buildCategory(slug, categoriesMap()[slug]);
}

export function listPosts(options?: { category?: string; limit?: number }): PostSummary[] {
  const slugs = orderedSlugs('blog_order');
  const map = blogsMap();
  const seen = new Set<string>();

  let posts = slugs
    .map((slug) => {
      seen.add(slug);
      return buildPostSummary(slug, map[slug]);
    })
    .filter((post): post is PostSummary => post != null && isPublished(post.publishedAt));

  const remainder = Object.keys(map)
    .filter((slug) => !seen.has(slug))
    .map((slug) => buildPostSummary(slug, map[slug]))
    .filter((post): post is PostSummary => post != null && isPublished(post.publishedAt));

  posts = [...posts, ...remainder].sort(
    (left, right) => new Date(right.publishedAt).getTime() - new Date(left.publishedAt).getTime(),
  );

  if (options?.category) {
    posts = posts.filter((post) => post.category?.slug === options.category);
  }

  if (options?.limit != null) {
    posts = posts.slice(0, options.limit);
  }

  return posts;
}

export function getPost(slug: string): Post | null {
  const raw = blogsMap()[slug];
  const summary = buildPostSummary(slug, raw);
  if (!summary || !raw?.published_at || !isPublished(raw.published_at)) return null;

  const body = buildContentBlocks(raw.body);
  const relatedProduct = raw.related_product
    ? buildProductSummary(raw.related_product, productsMap()[raw.related_product])
    : null;

  const relatedPosts = listPosts({ category: raw.category })
    .filter((post) => post.slug !== slug)
    .slice(0, 3);

  return {
    ...summary,
    body,
    readingMinutes: readingMinutes(body),
    tags: Array.isArray(raw.tags) ? raw.tags.map(String) : [],
    relatedProduct,
    relatedPosts,
  };
}

export function getPage(slug: string): StaticPageContent | null {
  const raw = pagesMap()[slug];
  if (!raw || isMissing(raw.title)) return null;

  return {
    slug,
    title: raw.title!,
    subtitle: raw.subtitle ?? '',
    updatedOn: raw.updated_on ?? '',
    body: buildContentBlocks(raw.body),
    path: pagePath(slug),
  };
}

export function getShared(): SharedData {
  return {
    brand: getBrand(),
    products: listProducts(),
    blogCategories: listCategories(),
  };
}

export function getHome(): HomeProps {
  const home = readObject<{
    hero?: HomeHero;
    advantages?: Advantage[];
    steps?: Step[];
    testimonials?: Testimonial[];
    faq?: FaqEntry[];
  }>('home');

  return {
    hero: home?.hero ?? { title: '', subtitle: '', highlights: [], stats: [] },
    advantages: home?.advantages ?? [],
    steps: home?.steps ?? [],
    testimonials: home?.testimonials ?? [],
    faq: home?.faq ?? [],
    posts: listPosts({ limit: 3 }),
  };
}

export function getContacts(): ContactsContent {
  const contacts = readObject<{
    intro?: string;
    channels?: ContactsContent['channels'];
    notes?: ContactsContent['notes'];
  }>('contacts');

  return {
    intro: contacts?.intro ?? '',
    channels: contacts?.channels ?? [],
    notes: contacts?.notes ?? [],
  };
}

export function getAudience(audience: Audience): AudienceProps {
  const section = readObject<{ heading?: string; subheading?: string }>(`audiences.${audience}`);
  const home = readObject<{
    advantages?: Advantage[];
    steps?: Step[];
    faq?: FaqEntry[];
  }>('home');

  return {
    heading: section?.heading ?? '',
    subheading: section?.subheading ?? '',
    audience,
    products: productsFor(audience),
    advantages: home?.advantages ?? [],
    steps: home?.steps ?? [],
    faq: home?.faq ?? [],
  };
}

export type { ContactsProps };

// Ensure locale is initialized when content helpers are imported outside DOMContentLoaded.
if (typeof document !== 'undefined' && document.body?.dataset.locale) {
  i18n.locale = document.body.dataset.locale;
}
